import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Claim, Note } from '@1po/1po-bff-fe-spec/generated/claim/response/model/Claim';
import { Table, Tooltip } from 'antd-v5';
import { v4 as uuidv4 } from 'uuid';
import { EnvelopeIcon, PaperClipIcon } from 'assets/icons';
import { useDisclosure } from 'components/Dialog';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import { useFetchStoredClaims } from 'domains/order/Order.requests';
import { fetchClaimsRequestSaga, getLoadedClaims } from 'domains/order/Order.store';
import { getClaimStatus, getReasonLabel, getTypeLabel } from 'pages/MyOrdersPage/OrderClaims/claimFormData';
import { ClaimDrawer } from 'pages/MyOrdersPage/OrderClaims/Drawer/ClaimDrawer';
import { ALL_DRAWER, DOCUMENTS_DRAWER, TimelineType } from 'pages/MyOrdersPage/OrderClaims/Drawer/ClaimDrawer.types';
import { theme } from 'styles';
import { Badge, CenterFlex, Icon, Image, InfiniteScroll } from 'UI';
import { getSearchData, isLoading, textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

// eslint-disable-next-line react/display-name
const ClaimsTable = ({ setTableWidth }: { setTableWidth: (width: number) => void }) => {
  const { t } = useTranslation();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const loadedClaims = useSelector(getLoadedClaims);
  const claimsData = getSearchData(loadedClaims);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const [selectedClaim, setSelectedClaim] = React.useState<Claim | null>(null);
  const [drawerTimeline, setDrawerTimeline] = React.useState<TimelineType>(ALL_DRAWER);

  useFetchStoredClaims();

  const loadMore = () => {
    if (isDelegationLoading) {
      return;
    }
    dispatch(fetchClaimsRequestSaga({ cursor: claimsData?.cursor }));
  };

  useEffect(() => {
    if (!tableWrapperRef.current) {
      return () => undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) {
        setTableWidth(width);
      }
    });

    observer.observe(tableWrapperRef.current);

    return () => observer.disconnect();
    // eslint-disable-next-line
  }, []);

  const disclosure = useDisclosure();

  const handleOpenDrawer = (claim: Claim, timelineType: TimelineType) => {
    setDrawerTimeline(timelineType);
    setSelectedClaim(claim);
    disclosure.onOpen();
  };

  const getColumns = () => [
    {
      title: t('order.claim.claim_id', 'Claim #'),
      dataIndex: 'externalClaimId',
      key: 'externalClaimId',
      render: (externalClaimId?: string) => <>{externalClaimId ?? ''}</>,
    },
    {
      title: t('order.claim.type', 'Type'),
      dataIndex: 'lastDocumentType',
      key: 'lastDocumentType',
      render: (lastDocumentType?: string) => <>{getTypeLabel(t, lastDocumentType)}</>,
    },
    {
      title: t('order.claim.order_id', 'Order #'),
      dataIndex: 'r1OrderId',
      key: 'r1OrderId',
      render: (r1OrderId?: string) => <>{r1OrderId}</>,
    },
    {
      title: t('order.claim.supplier_credit_debit_note_id', 'Credit/Debit note #'),
      dataIndex: 'note',
      key: 'note',
      render: (note?: Note) => {
        const displayPrice = textFormatter.formatCurrency(note?.noteValue ?? 0, note?.noteCurrency);
        return <Tooltip title={note?.noteValue && note?.noteCurrency && displayPrice}>{note?.noteId ?? ''}</Tooltip>;
      },
    },
    {
      title: t('order.claim.created_at', 'Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt?: string) => {
        if (!createdAt || !isValidDate(createdAt)) {
          return null;
        }
        return <>{textFormatter.formatTime(new Date(createdAt))}</>;
      },
    },
    {
      title: t('order.claim.reason', 'Reason'),
      dataIndex: 'code',
      key: 'code',
      render: (code?: string) => <>{getReasonLabel(t, code ?? '')}</>,
    },
    {
      title: t('order.claim.reference', 'Reference'),
      dataIndex: 'reference',
      key: 'reference',
      render: (reference?: string) => <>{reference ?? ''}</>,
    },
    {
      title: t('order.claim.designation', 'Designation'),
      dataIndex: 'referenceName',
      key: 'referenceName',
      render: (referenceName?: string) => <>{referenceName}</>,
    },
    {
      title: t('order.claim.quantity', 'Order Qty'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity?: string) => <>{quantity}</>,
    },
    {
      title: t('order.claim.net_value', 'Net value'),
      dataIndex: 'netValue',
      key: 'netValue',
      render: (_netValue: string, row: Claim) => {
        const { netValue, currency } = row;
        return <>{textFormatter.formatCurrency(netValue ?? 0, currency)}</>;
      },
    },
    {
      title: t('order.claim.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (_s: string, row?: Claim) => (
        <CenterFlex>{getClaimStatus(t, row?.status ?? '', row?.rejectionReasonLabel)}</CenterFlex>
      ),
    },
    {
      title: t('order.claim.messages', 'Messages'),
      dataIndex: 'supplierComments',
      key: 'supplierComments',
      render: (_s: string, row: Claim) => {
        const count = (row.supplierComments?.length ?? 0) + (row?.comment ? 1 : 0);
        return (
          <CenterFlex>
            {count > 0 && (
              <Badge
                size={15}
                offset={[13, 15]}
                count={count > 1 ? count : 0}
                color={theme.color.information_active}
                fontColor={theme.color.white}
              >
                <Icon IconComponent={EnvelopeIcon} size={24} onClick={() => handleOpenDrawer(row, ALL_DRAWER)} />
              </Badge>
            )}
          </CenterFlex>
        );
      },
    },
    {
      title: t('order.claim.documents', 'Documents'),
      dataIndex: 'supplierAttachmentsMessages',
      key: 'supplierAttachmentsMessages',
      render: (_s: string, row: Claim) => {
        const count = (row.supplierAttachmentsMessages?.length ?? 0) + (row.garageAttachments?.length ? 1 : 0);
        return (
          <CenterFlex>
            {count > 0 && (
              <Badge
                size={15}
                offset={[13, 15]}
                count={count > 1 ? count : 0}
                color={theme.color.information_active}
                fontColor={theme.color.white}
              >
                <Icon IconComponent={PaperClipIcon} size={24} onClick={() => handleOpenDrawer(row, DOCUMENTS_DRAWER)} />
              </Badge>
            )}
          </CenterFlex>
        );
      },
    },
  ];

  return (
    <>
      <InfiniteScroll loadMore={loadMore} hasMore={claimsData?.hasMore ?? false}>
        <div ref={tableWrapperRef}>
          <Table
            columns={getColumns()}
            dataSource={claimsData?.claims ?? []}
            locale={{ emptyText: <Image alt={'empty_placeholder'} src={'placeholder_empty.png'} width={375} /> }}
            loading={isLoading(loadedClaims.searchStatus)}
            pagination={false}
            rowKey={(record: Claim) => record?.claimId ?? `${uuidv4}`}
          />
        </div>
      </InfiniteScroll>
      <ClaimDrawer
        claim={selectedClaim}
        disclosure={disclosure}
        timelineType={drawerTimeline}
        onTimelineTypeChange={setDrawerTimeline}
      />
    </>
  );
};

export default ClaimsTable;
